import { useEffect, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Bell, Mail, Phone, Loader2 } from "lucide-react";

const CHANNEL_META = {
  email: { label: "Email", Icon: Mail },
  sms: { label: "SMS", Icon: Phone },
  push: { label: "Push", Icon: Bell },
};

export default function NotificationsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState({});
  const [phone, setPhone] = useState("");
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState("default");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    api.get("/notifications/preferences")
      .then((r) => {
        setData(r.data);
        setPrefs(r.data.prefs || {});
        setPhone(r.data.phone || "");
      })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));

    if ("serviceWorker" in navigator && "PushManager" in window) {
      setPushSupported(true);
      setPushPermission(Notification.permission || "default");
    }
  }, []);

  const toggle = (eventId, channel) => {
    setPrefs((p) => ({
      ...p,
      [eventId]: { ...p[eventId], [channel]: !p[eventId]?.[channel] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/notifications/preferences", { prefs, phone });
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const enablePush = async () => {
    if (!pushSupported) return toast.error("Push isn't supported in this browser");
    setSubscribing(true);
    try {
      // Ensure service worker registered
      const reg = await navigator.serviceWorker.register("/sw.js");
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== "granted") {
        toast.error("Push permission was blocked. Enable it in your browser settings.");
        return;
      }
      // Fetch VAPID public key from backend
      const { data: vapid } = await api.get("/notifications/vapid-public-key");
      if (!vapid.public_key) {
        toast.info("Permission granted — push channel will activate as soon as VAPID keys are configured on the server.");
        return;
      }
      const applicationServerKey = urlB64ToUint8Array(vapid.public_key);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      await api.post("/notifications/push/subscribe", {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent,
      });
      toast.success("Push notifications enabled on this device");
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const events = data?.events || {};
  const eventIds = Object.keys(events).filter((eid) => prefs[eid]);

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-display text-4xl text-charcoal">Notifications</h1>
        <p className="text-charcoal/70 mt-2">Pick how you want Move Day Pro&apos;s to reach you. You can change these anytime.</p>

        {/* Contact info */}
        <div className="mt-8 bg-[#131C2E] rounded-2xl border border-[#1F2A40] p-6 space-y-4">
          <h2 className="font-display text-xl text-charcoal">Contact info</h2>
          <div>
            <Label htmlFor="phone">Mobile number (required for SMS alerts)</Label>
            <Input
              id="phone"
              data-testid="notif-phone"
              placeholder="+1 555 123 4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="rounded-lg h-10 mt-1 max-w-xs"
            />
            <p className="text-[11px] text-charcoal/50 mt-1">Include the country code (e.g. +1 for US).</p>
          </div>
          <div className="pt-2 border-t border-[#1F2A40]">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <p className="text-sm text-charcoal font-medium flex items-center gap-2">
                  <Bell className="w-4 h-4 text-terracotta" /> Push on this device
                </p>
                <p className="text-[12px] text-charcoal/60 mt-0.5">
                  {pushSupported ? (
                    pushPermission === "granted" ? "Enabled — you'll get push here." :
                    pushPermission === "denied" ? "Blocked in your browser. Unblock in browser settings." :
                    "Not yet enabled."
                  ) : "Not supported in this browser."}
                </p>
              </div>
              {pushSupported && pushPermission !== "granted" && (
                <Button onClick={enablePush} disabled={subscribing} data-testid="enable-push-btn"
                  className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">
                  {subscribing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enabling…</> : "Enable push"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Preferences grid */}
        <div className="mt-6 bg-[#131C2E] rounded-2xl border border-[#1F2A40] overflow-hidden" data-testid="prefs-grid">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 items-center px-6 py-4 border-b border-[#1F2A40] text-xs uppercase tracking-widest text-charcoal/50">
            <span>Event</span>
            <span>Email</span>
            <span>SMS</span>
            <span>Push</span>
          </div>
          {eventIds.map((eid) => {
            const label = events[eid]?.label || eid;
            const row = prefs[eid] || {};
            return (
              <div key={eid} className="grid grid-cols-[1fr_auto_auto_auto] gap-x-6 items-center px-6 py-4 border-b border-[#1F2A40] last:border-b-0">
                <div>
                  <p className="text-charcoal text-sm font-medium">{label}</p>
                </div>
                {["email", "sms", "push"].map((ch) => {
                  const { Icon } = CHANNEL_META[ch];
                  return (
                    <div key={ch} className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-charcoal/40 hidden sm:block" />
                      <Switch
                        checked={!!row[ch]}
                        onCheckedChange={() => toggle(eid, ch)}
                        data-testid={`toggle-${eid}-${ch}`}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-prefs"
            className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11 px-8 btn-lift">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save preferences"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Utility — Base64URL → Uint8Array for Web Push
function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
